import { Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { useWorkspaces } from "@/shared/hooks/useWorkspaces";
import { cn } from "@/shared/ui/cn";
import { Dialog, DialogContent, DialogTitle } from "@/shared/ui/dialog";
import { Icon } from "@/shared/ui/icon";

interface Entry {
  id: string;
  label: string;
  hint?: string;
  to: string;
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * ⌘K (redesign 2026-09, pass 02 board 7b).
 *
 * **Two halves, and only one of them needs the network.** Screens and commands resolve locally from
 * the nav config against this session's permissions — which is exactly why navigation survives an
 * outage, and why the endpoint is four entities rather than six. People, events, devices and doors
 * come from `GET /search`, which is PR 0 of the migration and does not exist yet, so this renders
 * the state that pass was designed with: the local half works, and the server half says so plainly
 * rather than pretending to be empty.
 *
 * Nothing here filters by entitlement or role on its own — `useWorkspaces` has already resolved what
 * this caller may open, so a workspace they do not hold contributes no rows and its name never
 * appears. A palette that lists what you may not open is a leak and a dead end at once.
 */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { available, orgLevelItems } = useWorkspaces();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
    }
  }, [open]);

  /** Every screen this caller may open, named by its workspace so two "Reports" are told apart. */
  const screens = useMemo<Entry[]>(() => {
    const rows: Entry[] = [];
    for (const entry of available) {
      for (const item of entry.items) {
        if (item.disabledReason) continue;
        rows.push({
          id: `${entry.workspace.id}:${item.id}`,
          label: item.label,
          hint: entry.workspace.label,
          to: item.to,
        });
      }
    }
    for (const item of orgLevelItems) {
      if (!rows.some((r) => r.to === item.to)) {
        rows.push({ id: `org:${item.id}`, label: item.label, hint: "Organisation", to: item.to });
      }
    }
    return rows;
  }, [available, orgLevelItems]);

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return screens.slice(0, 8);
    const score = (e: Entry) => {
      const l = e.label.toLowerCase();
      if (l === q) return 0;
      if (l.startsWith(q)) return 1;
      if (l.includes(q)) return 2;
      return 3;
    };
    return screens
      .filter((e) => score(e) < 3)
      .sort((a, b) => score(a) - score(b) || a.label.localeCompare(b.label))
      .slice(0, 8);
  }, [screens, q]);

  useEffect(() => setActive(0), [q]);

  const run = (entry: Entry | undefined) => {
    if (!entry) return;
    onOpenChange(false);
    void navigate(entry.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(matches[active]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent width="md" className="top-[12vh] translate-y-0 gap-0 p-0">
        <DialogTitle className="sr-only">Search and jump to a screen</DialogTitle>
        <div className="flex items-center gap-2 border-b border-border px-3">
          <Icon icon={Search} size={16} className="text-fg-muted" />
          {/*
            No `autoFocus`: Radix's dialog focuses the first focusable child when it opens, and this
            input is it. A palette that does not take the caret is a dialog you have to click into,
            which would defeat the keystroke that opened it.
          */}
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Jump to a screen"
            aria-label="Jump to a screen"
            className="h-(--bar-h-page) w-full border-0 bg-transparent text-body text-fg outline-none placeholder:text-fg-faint"
          />
        </div>
        <ul ref={listRef} className="max-h-80 overflow-y-auto p-1.5">
          {matches.map((entry, i) => (
            <li key={entry.id}>
              <button
                type="button"
                onClick={() => run(entry)}
                onMouseEnter={() => setActive(i)}
                aria-current={i === active ? "true" : undefined}
                className={cn(
                  "flex h-(--nav-row-h) w-full items-center gap-2 rounded-sm px-2 text-left text-cell",
                  i === active ? "bg-accent-soft text-accent-fg" : "text-fg",
                )}
              >
                <span className="truncate font-medium">{entry.label}</span>
                {entry.hint ? (
                  <span className="ml-auto shrink-0 text-caption text-fg-faint">{entry.hint}</span>
                ) : null}
              </button>
            </li>
          ))}
          {matches.length === 0 ? (
            <li className="px-2 py-3 text-cell text-fg-muted">No screen matches “{query}”.</li>
          ) : null}
        </ul>
        {/*
          The designed "search unavailable" half, not an apology: it states which half is working and
          what the other one would answer, so a reader stops typing a person's name into it. It
          disappears the moment GET /search ships (PR 0, plans/v2/redesign-api.md).
        */}
        <p className="border-t border-border px-3 py-2 text-caption text-fg-muted">
          Screens only for now. Searching people, events, terminals and doors arrives with the search endpoint
          {can("people", "view") ? "; until then, the People page searches by name" : ""}.
        </p>
      </DialogContent>
    </Dialog>
  );
}
